import React, { useEffect, useState, useCallback } from "react";
import { api, authHeaders } from "@/lib/api";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Loader2, Download, TrendingUp, Store, Users, Ticket, PiggyBank, Trophy, Activity, Repeat, UserMinus, MapPin, Globe, Cake, Mail, Send, Filter, MessageCircle, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { COUNTRIES, GENDER_OPTIONS } from "@/lib/countries";

const AGE_BUCKETS = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+", "<18", "Non specificato"];

const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" }) + " " + d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
};

const segLabel = (f) => {
  if (!f) return "Tutti i clienti";
  const parts = [];
  if (f.city) parts.push(f.city);
  if (f.country) parts.push(f.country);
  if (f.gender) { const g = GENDER_OPTIONS.find((o) => o.value === f.gender); parts.push(g ? g.label : f.gender); }
  if (f.age_bucket) parts.push(f.age_bucket);
  return parts.length ? parts.join(" · ") : "Tutti i clienti";
};

const RANGES = [{ v: 7, l: "7g" }, { v: 30, l: "30g" }, { v: 90, l: "90g" }];
const EXPORTS = [
  { k: "clients", l: "Clienti" }, { k: "vendors", l: "Venditori" }, { k: "offers", l: "Offerte" },
  { k: "claims", l: "Coupon" }, { k: "reviews", l: "Recensioni" },
];

const shortDate = (d) => d.slice(8) + "/" + d.slice(5, 7);

const HBar = ({ title, icon: Icon, data, color = "#E63946", testId }) => (
  <div className="kn-card p-4" data-testid={testId}>
    <p className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-1.5"><Icon className="h-4 w-4 text-[#E63946]" /> {title}</p>
    {(!data || data.length === 0) ? <p className="text-sm text-slate-400 py-2 text-center">Nessun dato</p> : (
      <ResponsiveContainer width="100%" height={Math.max(120, data.length * 36)}>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
          <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={110} interval={0} />
          <Tooltip />
          <Bar dataKey="count" name="Clienti" fill={color} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    )}
  </div>
);

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seg, setSeg] = useState({ city: "", country: "", gender: "", age_bucket: "", only_email_optin: true });
  const [segCount, setSegCount] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [waBody, setWaBody] = useState("");
  const [waSending, setWaSending] = useState(false);
  const [waPrev, setWaPrev] = useState(null);
  const [campaigns, setCampaigns] = useState([]);

  const loadCampaigns = useCallback(() => {
    api.get("/admin/campaigns", { headers: authHeaders("admin") })
      .then((r) => setCampaigns(r.data.campaigns || [])).catch(() => {});
  }, []);

  const setF = (k, v) => { setSeg((s) => ({ ...s, [k]: v })); setSegCount(null); setWaPrev(null); };

  const previewSegment = async () => {
    setPreviewing(true);
    try {
      const r = await api.post("/admin/segment/preview", seg, { headers: authHeaders("admin") });
      setSegCount(r.data.count);
    } catch (_) { toast.error("Errore anteprima"); }
    finally { setPreviewing(false); }
  };

  const sendBroadcast = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Inserisci oggetto e messaggio");
    setSending(true);
    try {
      const r = await api.post("/admin/broadcast/email", { ...seg, subject: subject.trim(), body }, { headers: authHeaders("admin") });
      toast.success(`Email inviata a ${r.data.sent} clienti${r.data.failed ? ` (${r.data.failed} non riuscite)` : ""}`);
      setSubject(""); setBody(""); setSegCount(null);
      loadCampaigns();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore invio"); }
    finally { setSending(false); }
  };

  const previewWa = async () => {
    try {
      const r = await api.post("/admin/segment/whatsapp-preview", seg, { headers: authHeaders("admin") });
      setWaPrev(r.data);
    } catch (_) { toast.error("Errore anteprima"); }
  };

  const sendWa = async () => {
    if (!waBody.trim()) return toast.error("Inserisci il messaggio WhatsApp");
    setWaSending(true);
    try {
      const r = await api.post("/admin/broadcast/whatsapp", { ...seg, body: waBody }, { headers: authHeaders("admin") });
      if (r.data.sent > 0) toast.success(`WhatsApp inviato a ${r.data.sent} numeri${r.data.failed ? ` (${r.data.failed} non riusciti)` : ""}`);
      else toast.warning(`Nessun invio riuscito (${r.data.failed} falliti). Verifica che i numeri si siano iscritti alla sandbox.`);
      if (r.data.sent > 0) setWaBody("");
      loadCampaigns();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore invio"); }
    finally { setWaSending(false); }
  };

  useEffect(() => {
    setLoading(true);
    api.get("/admin/analytics", { headers: authHeaders("admin"), params: { days } })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const exportCsv = async (entity) => {
    try {
      const r = await api.get(`/admin/export/${entity}`, { headers: authHeaders("admin"), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `koupnow_${entity}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Esportato ${entity}.csv (apribile in Excel)`);
    } catch (_) { toast.error("Errore esportazione"); }
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#E63946]" /></div>;
  if (!data) return <div className="kn-card p-8 text-center text-slate-400">Nessun dato</div>;

  const t = data.totals;
  const regData = data.registrations.vendors.map((v, i) => ({ date: shortDate(v.date), Venditori: v.value, Clienti: data.registrations.clients[i]?.value || 0 }));
  const claimData = data.claims.map((c, i) => ({ date: shortDate(c.date), Richiesti: c.value, Usati: data.redeemed[i]?.value || 0 }));

  const cards = [
    { l: "Venditori", v: t.vendors, icon: Store }, { l: "Clienti", v: t.clients, icon: Users },
    { l: "Coupon", v: t.claims, icon: Ticket }, { l: "Riscattati", v: t.redeemed, icon: Ticket },
    { l: "Incasso €", v: t.revenue, icon: PiggyBank }, { l: "Risparmi €", v: t.savings, icon: PiggyBank },
  ];

  return (
    <div className="space-y-5" data-testid="admin-analytics">
      <div className="flex items-center justify-between">
        <div className="flex gap-1.5">
          {RANGES.map((r) => (
            <button key={r.v} onClick={() => setDays(r.v)} data-testid={`range-${r.v}`}
              className={`px-3 h-8 rounded-full text-xs font-bold ${days === r.v ? "kn-gradient text-white" : "bg-slate-100 text-slate-600"}`}>{r.l}</button>
          ))}
        </div>
      </div>

      <div className="kn-card px-4 py-3 grid grid-cols-3 sm:grid-cols-6 gap-y-3" data-testid="ana-cards">
        {cards.map((c) => (
          <div key={c.l} className="text-center px-1" data-testid={`ana-card-${c.l}`}>
            <p className="text-lg font-extrabold text-slate-900 leading-none">{c.v}</p>
            <p className="text-[11px] text-slate-500 mt-1">{c.l}</p>
          </div>
        ))}
      </div>

      {data.retention && (
        <div className="kn-card p-4" data-testid="retention-block">
          <p className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-1.5"><Activity className="h-4 w-4 text-slate-400" /> Utenti attivi & ritorno</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
            <div><p className="text-lg font-extrabold text-slate-900 leading-none">{data.retention.active_clients_7d}</p><p className="text-[11px] text-slate-500 mt-1">Clienti attivi (7g)</p></div>
            <div><p className="text-lg font-extrabold text-slate-900 leading-none">{data.retention.active_clients_30d}</p><p className="text-[11px] text-slate-500 mt-1">Clienti attivi (30g)</p></div>
            <div><p className="text-lg font-extrabold text-slate-900 leading-none">{data.retention.active_vendors_7d}</p><p className="text-[11px] text-slate-500 mt-1">Venditori attivi (7g)</p></div>
            <div><p className="text-lg font-extrabold text-slate-900 leading-none">{data.retention.active_vendors_30d}</p><p className="text-[11px] text-slate-500 mt-1">Venditori attivi (30g)</p></div>
            <div><p className="text-lg font-extrabold text-slate-900 leading-none">{data.retention.returning_clients} <span className="text-xs font-bold text-slate-400">({data.retention.returning_rate}%)</span></p><p className="text-[11px] text-slate-500 mt-1">Clienti di ritorno</p></div>
            <div><p className="text-lg font-extrabold text-slate-900 leading-none">{data.retention.stickiness}%</p><p className="text-[11px] text-slate-500 mt-1">Stickiness (WAU/MAU)</p></div>
          </div>
          <p className="text-[11px] text-slate-400 mt-3">Nuovi clienti: {data.retention.new_clients_7d} (7g) · {data.retention.new_clients_30d} (30g). "Di ritorno" = ha usato l'app in ≥2 giorni diversi.</p>
        </div>
      )}

      {data.deletions && (
        <div className="kn-card p-4" data-testid="deletion-reasons-block">
          <p className="font-bold text-slate-800 text-sm mb-1 flex items-center gap-1.5"><UserMinus className="h-4 w-4 text-[#E63946]" /> Motivi di cancellazione account</p>
          <p className="text-[11px] text-slate-400 mb-3">{data.deletions.voluntary_total} volontarie · {data.deletions.admin_total} da admin</p>
          {(!data.deletions.reasons || data.deletions.reasons.length === 0) ? (
            <p className="text-sm text-slate-400 py-3 text-center" data-testid="deletion-reasons-empty">Nessuna cancellazione registrata.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(140, data.deletions.reasons.length * 38)}>
              <BarChart data={data.deletions.reasons} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis type="category" dataKey="reason" tick={{ fontSize: 10 }} width={120} interval={0} />
                <Tooltip />
                <Bar dataKey="count" name="Cancellazioni" fill="#E63946" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {data.demographics && (
        <>
          <div className="grid grid-cols-1 gap-3">
            <HBar title="Clienti per genere" icon={Users} data={data.demographics.by_gender} color="#E63946" testId="demo-gender" />
            <HBar title="Clienti per fascia d'età" icon={Cake} data={data.demographics.by_age} color="#F4A261" testId="demo-age" />
            <HBar title="Clienti per città" icon={MapPin} data={data.demographics.by_city} color="#2A9D8F" testId="demo-city" />
            <HBar title="Clienti per nazione" icon={Globe} data={data.demographics.by_country} color="#457B9D" testId="demo-country" />
          </div>

          <div className="kn-card p-4" data-testid="broadcast-tool">
            <p className="font-bold text-slate-800 text-sm mb-1 flex items-center gap-1.5"><Send className="h-4 w-4 text-[#E63946]" /> Invia email ai clienti (segmentata)</p>
            <p className="text-[11px] text-slate-400 mb-3">Filtra i destinatari e invia un messaggio email diverso per ogni segmento.</p>
            <div className="rounded-xl bg-slate-50 p-3 space-y-2.5">
              <p className="text-xs font-bold text-slate-500 flex items-center gap-1.5"><Filter className="h-3.5 w-3.5" /> Filtri</p>
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="seg-city" value={seg.city} onChange={(e) => setF("city", e.target.value)} placeholder="Città (tutte)"
                  className="h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white" />
                <select data-testid="seg-country" value={seg.country} onChange={(e) => setF("country", e.target.value)}
                  className="h-10 rounded-lg border border-slate-200 px-2 text-sm bg-white">
                  <option value="">Nazione (tutte)</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select data-testid="seg-gender" value={seg.gender} onChange={(e) => setF("gender", e.target.value)}
                  className="h-10 rounded-lg border border-slate-200 px-2 text-sm bg-white">
                  <option value="">Genere (tutti)</option>
                  {GENDER_OPTIONS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
                <select data-testid="seg-age" value={seg.age_bucket} onChange={(e) => setF("age_bucket", e.target.value)}
                  className="h-10 rounded-lg border border-slate-200 px-2 text-sm bg-white">
                  <option value="">Età (tutte)</option>
                  {AGE_BUCKETS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <label className="flex items-center justify-between text-xs text-slate-600">
                Solo chi accetta email promozionali
                <input type="checkbox" data-testid="seg-optin" checked={seg.only_email_optin} onChange={(e) => setF("only_email_optin", e.target.checked)} className="h-4 w-4 accent-[#E63946]" />
              </label>
              <button onClick={previewSegment} disabled={previewing} data-testid="seg-preview-btn"
                className="w-full h-9 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-bold flex items-center justify-center gap-1.5">
                {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />} Anteprima destinatari
              </button>
              {segCount !== null && (
                <p className="text-center text-sm font-bold text-[#E63946]" data-testid="seg-count">{segCount} destinatari corrispondono ai filtri</p>
              )}
            </div>
            <div className="mt-3 space-y-2.5">
              <input data-testid="broadcast-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Oggetto dell'email"
                className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm" />
              <textarea data-testid="broadcast-body" value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Scrivi il messaggio…"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm resize-y" />
              <button onClick={sendBroadcast} disabled={sending} data-testid="broadcast-send-btn"
                className="w-full h-11 rounded-xl kn-gradient text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Invia email al segmento
              </button>
            </div>
          </div>

          <div className="kn-card p-4" data-testid="whatsapp-tool">
            <p className="font-bold text-slate-800 text-sm mb-1 flex items-center gap-1.5"><MessageCircle className="h-4 w-4 text-[#25D366]" /> Invia WhatsApp ai clienti (segmentato)</p>
            <p className="text-[11px] text-slate-400 mb-3">Usa gli stessi filtri qui sopra. Invia un messaggio WhatsApp ai clienti con numero valido.</p>
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-2.5 mb-3">
              <p className="text-[11px] text-amber-800">⚠️ Account Twilio in modalità <b>Sandbox</b>: il messaggio arriva solo ai numeri che si sono iscritti alla sandbox (inviando <b>join &lt;codice&gt;</b> al numero Twilio). Per inviare a tutti i clienti reali serve l'upgrade a un mittente WhatsApp di produzione.</p>
            </div>
            <p className="text-[11px] text-slate-500 mb-2">Invia solo ai clienti che hanno <b>attivato "Messaggi WhatsApp"</b> nel profilo (opt-in).</p>
            <button onClick={previewWa} data-testid="wa-preview-btn"
              className="w-full h-9 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-bold flex items-center justify-center gap-1.5 mb-2">
              <Users className="h-4 w-4" /> Anteprima destinatari WhatsApp
            </button>
            {waPrev && (
              <p className="text-center text-sm font-bold text-[#25D366] mb-2" data-testid="wa-count">{waPrev.reachable} numeri raggiungibili su {waPrev.count} clienti nel segmento</p>
            )}
            <textarea data-testid="wa-body" value={waBody} onChange={(e) => setWaBody(e.target.value)} rows={4} placeholder="Scrivi il messaggio WhatsApp…"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm resize-y mb-2" />
            <button onClick={sendWa} disabled={waSending} data-testid="wa-send-btn"
              className="w-full h-11 rounded-xl bg-[#25D366] hover:bg-[#1eba57] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
              {waSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />} Invia WhatsApp al segmento
            </button>
          </div>

          <div className="kn-card p-4" data-testid="campaigns-history">
            <p className="font-bold text-slate-800 text-sm mb-1 flex items-center gap-1.5"><ScrollText className="h-4 w-4 text-[#E63946]" /> Storico invii</p>
            <p className="text-[11px] text-slate-400 mb-3">Le ultime campagne inviate (email e WhatsApp).</p>
            {campaigns.length === 0 ? (
              <p className="text-sm text-slate-400 py-2 text-center" data-testid="campaigns-empty">Nessun invio effettuato.</p>
            ) : (
              <div className="space-y-2">
                {campaigns.map((c) => (
                  <div key={c.campaign_id} data-testid={`campaign-${c.campaign_id}`} className="flex items-start gap-3 p-3 rounded-xl border border-slate-100">
                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${c.channel === "whatsapp" ? "bg-[#25D366]/15" : "bg-[#E63946]/10"}`}>
                      {c.channel === "whatsapp" ? <MessageCircle className="h-4 w-4 text-[#25D366]" /> : <Mail className="h-4 w-4 text-[#E63946]" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-bold text-slate-800 text-sm truncate">{c.channel === "whatsapp" ? "WhatsApp" : (c.subject || "Email")}</p>
                        <span className="text-[10px] text-slate-400 shrink-0">{fmtDateTime(c.created_at)}</span>
                      </div>
                      <p className="text-[11px] text-slate-500 truncate">{c.body_preview}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{segLabel(c.filters)}</span>
                        <span className="text-[10px] font-bold text-emerald-600">{c.sent}/{c.total} raggiunti{c.failed ? ` · ${c.failed} falliti` : ""}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <div className="kn-card p-4">
        <p className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-[#E63946]" /> Registrazioni</p>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={regData} margin={{ left: -20, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
            <Tooltip />
            <Line type="monotone" dataKey="Venditori" stroke="#E63946" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="Clienti" stroke="#2A9D8F" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="kn-card p-4">
        <p className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-1.5"><Ticket className="h-4 w-4 text-[#E63946]" /> Coupon nel tempo</p>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={claimData} margin={{ left: -20, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="Richiesti" fill="#F4A261" radius={[3, 3, 0, 0]} />
            <Bar dataKey="Usati" fill="#E63946" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="kn-card p-4">
        <p className="font-bold text-slate-800 text-sm mb-2 flex items-center gap-1.5"><Store className="h-4 w-4 text-[#E63946]" /> Top negozi (coupon)</p>
        {data.top_stores.length === 0 ? <p className="text-sm text-slate-400 py-2">Nessun dato</p> :
          data.top_stores.map((s, i) => (
            <div key={s.vendor_id} className="flex items-center gap-2 py-1.5 border-b border-slate-50 last:border-0">
              <span className="text-xs font-bold text-slate-400 w-5">{i + 1}</span>
              <span className="text-sm font-semibold text-slate-700 truncate flex-1">{s.store_name}</span>
              <span className="text-sm font-bold text-[#E63946]">{s.claims}</span>
            </div>
          ))}
      </div>

      <div className="kn-card p-4">
        <p className="font-bold text-slate-800 text-sm mb-2 flex items-center gap-1.5"><Trophy className="h-4 w-4 text-amber-500" /> Classifica inviti (referral)</p>
        {data.referral_leaderboard.length === 0 ? <p className="text-sm text-slate-400 py-2">Nessun invito ancora</p> :
          data.referral_leaderboard.map((r, i) => (
            <div key={r.code} className="flex items-center gap-2 py-1.5 border-b border-slate-50 last:border-0" data-testid={`ref-rank-${i}`}>
              <span className="text-xs font-bold text-slate-400 w-5">{i + 1}</span>
              <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-[#FFE4E6] text-[#BE123C]">{r.code}</span>
              <span className="text-sm font-semibold text-slate-700 truncate flex-1">{r.name}</span>
              <span className="text-sm font-bold text-[#E63946]">{r.invited}</span>
            </div>
          ))}
      </div>

      <div className="kn-card p-4">
        <p className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-1.5"><Download className="h-4 w-4 text-[#E63946]" /> Esporta dati (CSV / Excel)</p>
        <div className="flex flex-wrap gap-2">
          {EXPORTS.map((e) => (
            <button key={e.k} onClick={() => exportCsv(e.k)} data-testid={`export-${e.k}`}
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 hover:bg-slate-50">
              <Download className="h-4 w-4" /> {e.l}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
